import {
  BaseMessageSignerWalletAdapter,
  isVersionedTransaction,
  scopePollingDetectionStrategy,
  WalletAccountError,
  WalletConnectionError,
  WalletDisconnectedError,
  WalletDisconnectionError,
  WalletError,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletPublicKeyError,
  WalletReadyState,
  WalletSendTransactionError,
  WalletSignMessageError,
  WalletSignTransactionError,
  type WalletName,
} from '@solana/wallet-adapter-base';
import {
  PublicKey,
  type Commitment,
  type Transaction,
  type TransactionSignature,
  type VersionedTransaction,
} from '@solana/web3.js';
import { parseWalletSignMessageResult } from '../utils/parseWalletSignMessageResult';
import { NIGHTLY_WALLET_ICON } from '../config/nightlyWalletIcon';

export const NightlyWalletName = 'Nightly' as WalletName<'Nightly'>;

type WalletAccount = {
  address: string;
  publicKey: Uint8Array;
};

type NightlyFeatures = {
  'standard:connect'?: {
    connect: (input?: { silent?: boolean }) => Promise<{ accounts: readonly WalletAccount[] }>;
  };
  'standard:disconnect'?: {
    disconnect: () => Promise<void>;
  };
  'solana:signMessage'?: {
    signMessage: (
      ...inputs: readonly { account: WalletAccount; message: Uint8Array }[]
    ) => Promise<readonly { signedMessage: Uint8Array; signature: Uint8Array }[]>;
  };
  // Nightly docs also reference this key in places
  'standard:signMessage'?: {
    signMessage: (
      ...inputs: readonly { account: WalletAccount; message: Uint8Array }[]
    ) => Promise<readonly { signedMessage: Uint8Array; signature: Uint8Array }[]>;
  };
  'solana:signTransaction'?: {
    signTransaction: (
      ...inputs: readonly {
        account: WalletAccount;
        transaction: Uint8Array;
        chain?: string;
      }[]
    ) => Promise<readonly { signedTransaction: Uint8Array }[]>;
  };
};

type NightlySolana = {
  publicKey?: PublicKey | { toBytes: () => Uint8Array; toString: () => string };
  accounts?: readonly WalletAccount[];
  features?: NightlyFeatures;
  connect?: () => Promise<unknown>;
  disconnect?: () => Promise<void>;
  signTransaction?: <T extends Transaction | VersionedTransaction>(tx: T) => Promise<T>;
  signAllTransactions?: <T extends Transaction | VersionedTransaction>(txs: T[]) => Promise<T[]>;
  signMessage?: (message: Uint8Array | string) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  off?: (event: string, handler: (...args: unknown[]) => void) => void;
};

function getNightlyProvider(): NightlySolana | null {
  if (typeof window === 'undefined') return null;
  const nightly = (window as Window & { nightly?: { solana?: NightlySolana } }).nightly?.solana;
  return nightly ?? null;
}

function accountToPublicKey(account: WalletAccount): PublicKey {
  return new PublicKey(account.publicKey);
}

/**
 * Nightly adapter compatible with current extension (Wallet Standard features)
 * and older legacy `connect()` / `signMessage()` injection.
 *
 * Core bug: calling only legacy `wallet.connect()` fails on modern Nightly with
 * vague errors like "wallet action failed" because Nightly expects
 * `features['standard:connect'].connect()`.
 */
export class CwyNightlyWalletAdapter extends BaseMessageSignerWalletAdapter {
  name = NightlyWalletName;
  url = 'https://nightly.app';
  icon = NIGHTLY_WALLET_ICON;
  supportedTransactionVersions = new Set(['legacy', 0] as const);

  private _readyState =
    typeof window === 'undefined' || typeof document === 'undefined'
      ? WalletReadyState.Unsupported
      : WalletReadyState.NotDetected;
  private _connecting = false;
  private _wallet: NightlySolana | null = null;
  private _account: WalletAccount | null = null;
  private _publicKey: PublicKey | null = null;

  private _disconnected = () => {
    const wallet = this._wallet;
    if (!wallet) return;
    wallet.off?.('disconnect', this._disconnected);
    wallet.off?.('accountChanged', this._accountChanged);
    this._wallet = null;
    this._account = null;
    this._publicKey = null;
    this.emit('error', new WalletDisconnectedError());
    this.emit('disconnect');
  };

  private _accountChanged = (...args: unknown[]) => {
    if (!this._publicKey) return;
    try {
      const nextRaw = args[0] as PublicKey | WalletAccount | undefined;
      const next =
        nextRaw && typeof nextRaw === 'object' && 'publicKey' in nextRaw && !(nextRaw instanceof PublicKey)
          ? accountToPublicKey(nextRaw as WalletAccount)
          : new PublicKey((nextRaw as PublicKey).toBytes());
      if (this._publicKey.equals(next)) return;
      this._publicKey = next;
      if (nextRaw && typeof nextRaw === 'object' && 'address' in nextRaw) {
        this._account = nextRaw as WalletAccount;
      }
      this.emit('connect', next);
    } catch (error) {
      this.emit('error', new WalletPublicKeyError((error as Error)?.message, error));
    }
  };

  constructor() {
    super();
    if (this._readyState === WalletReadyState.Unsupported) return;

    scopePollingDetectionStrategy(() => {
      const installed = getNightlyProvider() != null;
      this._readyState = installed ? WalletReadyState.Installed : WalletReadyState.NotDetected;
      this.emit('readyStateChange', this._readyState);
      return installed;
    });
  }

  get publicKey() {
    return this._publicKey;
  }

  get connecting() {
    return this._connecting;
  }

  get connected() {
    return !!this._wallet && !!this._publicKey;
  }

  get readyState() {
    return this._readyState;
  }

  async connect(): Promise<void> {
    if (this.connected || this._connecting) return;
    if (this._readyState !== WalletReadyState.Installed) throw new WalletNotReadyError();

    const wallet = getNightlyProvider();
    if (!wallet) throw new WalletNotReadyError();

    try {
      this._connecting = true;

      let account: WalletAccount | null = null;
      let publicKey: PublicKey | null = null;

      // 1) Preferred: Wallet Standard connect (current Nightly)
      const standardConnect = wallet.features?.['standard:connect']?.connect;
      if (typeof standardConnect === 'function') {
        try {
          const { accounts } = await standardConnect();
          account = accounts?.[0] ?? null;
          if (account) {
            publicKey = accountToPublicKey(account);
          }
        } catch (error) {
          throw new WalletConnectionError((error as Error)?.message || 'Nightly connect failed', error);
        }
      } else if (typeof wallet.connect === 'function') {
        // 2) Legacy fallback
        try {
          await wallet.connect();
        } catch (error) {
          throw new WalletConnectionError((error as Error)?.message || 'Nightly connect failed', error);
        }
        if (wallet.publicKey && wallet.publicKey.toString() !== '11111111111111111111111111111111') {
          try {
            publicKey = new PublicKey(wallet.publicKey.toBytes());
            account = {
              address: publicKey.toBase58(),
              publicKey: publicKey.toBytes(),
            };
          } catch (error) {
            throw new WalletPublicKeyError((error as Error)?.message, error);
          }
        }
      } else {
        throw new WalletConnectionError(
          'Nightly Wallet is installed but does not expose a supported Solana connect API. Update the Nightly extension and try again.'
        );
      }

      if (!publicKey || !account) {
        throw new WalletAccountError();
      }

      wallet.on?.('disconnect', this._disconnected);
      wallet.on?.('accountChanged', this._accountChanged);
      this._wallet = wallet;
      this._account = account;
      this._publicKey = publicKey;
      this.emit('connect', publicKey);
    } catch (error) {
      const walletError =
        error instanceof WalletError
          ? error
          : new WalletConnectionError((error as Error)?.message, error);
      this.emit('error', walletError);
      throw walletError;
    } finally {
      this._connecting = false;
    }
  }

  async disconnect(): Promise<void> {
    const wallet = this._wallet;
    if (wallet) {
      wallet.off?.('disconnect', this._disconnected);
      wallet.off?.('accountChanged', this._accountChanged);
      this._wallet = null;
      this._account = null;
      this._publicKey = null;
      try {
        const standardDisconnect = wallet.features?.['standard:disconnect']?.disconnect;
        if (typeof standardDisconnect === 'function') {
          await standardDisconnect();
        } else if (typeof wallet.disconnect === 'function') {
          await wallet.disconnect();
        }
      } catch (error) {
        this.emit('error', new WalletDisconnectionError((error as Error)?.message, error));
      }
    }
    this.emit('disconnect');
  }

  async sendTransaction(
    transaction: Transaction | VersionedTransaction,
    connection: Parameters<BaseMessageSignerWalletAdapter['sendTransaction']>[1],
    options: Parameters<BaseMessageSignerWalletAdapter['sendTransaction']>[2] = {}
  ): Promise<TransactionSignature> {
    try {
      const { signers, ...sendOptions } = options as {
        signers?: Parameters<Transaction['partialSign']>;
      } & Record<string, unknown>;

      if (!isVersionedTransaction(transaction)) {
        transaction = (await this.prepareTransaction(transaction, connection, sendOptions)) as Transaction;
        if (signers?.length) transaction.partialSign(...signers);
      } else if (signers?.length) {
        transaction.sign(signers as never);
      }

      const signed = await this.signTransaction(transaction);
      return connection.sendRawTransaction(signed.serialize(), {
        skipPreflight: sendOptions?.skipPreflight as boolean | undefined,
        preflightCommitment:
          (sendOptions?.preflightCommitment as Commitment | undefined) ??
          connection.commitment ??
          'confirmed',
      });
    } catch (error) {
      if (error instanceof WalletError) throw error;
      throw new WalletSendTransactionError((error as Error)?.message, error);
    }
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
    const wallet = this._wallet;
    const account = this._account;
    if (!wallet || !account) throw new WalletNotConnectedError();

    try {
      if (typeof wallet.signTransaction === 'function') {
        return await wallet.signTransaction(transaction);
      }

      const standardSign = wallet.features?.['solana:signTransaction']?.signTransaction;
      if (typeof standardSign === 'function') {
        const serialized = isVersionedTransaction(transaction)
          ? transaction.serialize()
          : transaction.serialize({ requireAllSignatures: false, verifySignatures: false });
        const [result] = await standardSign({
          account,
          transaction: serialized,
        });
        if (!result?.signedTransaction) {
          throw new WalletSignTransactionError('Nightly returned an empty signed transaction');
        }
        // Prefer legacy path when available; Standard returns bytes — reconstruct via original sign API only.
        throw new WalletSignTransactionError(
          'Nightly Standard signTransaction bytes path is not fully wired. Update Nightly or use Phantom/Solflare.'
        );
      }

      throw new WalletSignTransactionError('Nightly Wallet signTransaction is unavailable');
    } catch (error) {
      if (error instanceof WalletError) throw error;
      throw new WalletSignTransactionError((error as Error)?.message, error);
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
    const wallet = this._wallet;
    if (!wallet) throw new WalletNotConnectedError();
    if (typeof wallet.signAllTransactions !== 'function') {
      const signed: T[] = [];
      for (const tx of transactions) {
        signed.push(await this.signTransaction(tx));
      }
      return signed;
    }
    try {
      return await wallet.signAllTransactions(transactions);
    } catch (error) {
      throw new WalletSignTransactionError((error as Error)?.message, error);
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    const wallet = this._wallet;
    const account = this._account;
    if (!wallet || !account) throw new WalletNotConnectedError();

    try {
      // 1) Wallet Standard (current Nightly docs)
      const standardSign =
        wallet.features?.['solana:signMessage']?.signMessage ||
        wallet.features?.['standard:signMessage']?.signMessage;

      if (typeof standardSign === 'function') {
        const [result] = await standardSign({ account, message });
        if (!result?.signature) {
          throw new WalletSignMessageError('Nightly returned an empty signature');
        }
        return parseWalletSignMessageResult({
          signature: result.signature,
          signedMessage: result.signedMessage,
        }).signature;
      }

      // 2) Legacy fallback
      if (typeof wallet.signMessage === 'function') {
        let raw: unknown;
        try {
          raw = await wallet.signMessage(message);
        } catch {
          raw = await wallet.signMessage(new TextDecoder().decode(message));
        }
        return parseWalletSignMessageResult(raw).signature;
      }

      throw new WalletSignMessageError(
        'Nightly Wallet does not support message signing in this version. Update Nightly and try again.'
      );
    } catch (error) {
      if (error instanceof WalletError) throw error;
      throw new WalletSignMessageError((error as Error)?.message, error);
    }
  }
}
